import React, { useState } from "react";
import { invoiceRepo, receiptRepo, taxDocumentRepo, ORDER_TYPE } from "../../services";
import { settleInvoicePayment } from "../../services/settlement";
import { onlinePaymentService } from "../../services/onlinePayment";
import { TAX_DOC_TYPES } from "../../services/taxDocument";
import TaxDocumentPaper, { PrintArea } from "../../components/TaxDocumentPaper";
import { useBranding } from "../../config/branding";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, Card, EmptyState, Modal, StatusBadge, Tabs, baht, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function CustomerBilling() {
  const { orgId, organization, toast, hasModule } = useApp();
  const brand = useBranding();
  const [taxView, setTaxView] = useState(null);
  const [printToken, setPrintToken] = useState(0);
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);
  const [tab, setTab] = useState("unpaid");
  const [payInvoice, setPayInvoice] = useState(null);

  const { data: invoices, reload } = useAsync(
    () => (customer ? invoiceRepo.all(orgId, (row) => row.customerId === customer.id) : Promise.resolve([])),
    [orgId, customer],
    []
  );
  const { data: receipts } = useAsync(
    () => (customer ? receiptRepo.all(orgId, (row) => row.customerId === customer.id) : Promise.resolve([])),
    [orgId, customer],
    []
  );

  // ใบกำกับภาษีของตัวเองเท่านั้น (ออกแล้ว/ส่งสรรพากร — ไม่โชว์ฉบับร่างและที่ยกเลิก)
  const { data: taxDocs } = useAsync(
    () => (customer ? taxDocumentRepo.all(orgId, (row) => row.customerId === customer.id && ["issued", "pending_rd"].includes(row.status)) : Promise.resolve([])),
    [orgId, customer],
    []
  );
  // ปุ่มจ่ายออนไลน์: ร้านต้องมีโมดูล online_payment และไม่ได้ปิดรับในตั้งค่า
  const { data: paySettings } = useAsync(() => onlinePaymentService.getSettings(orgId), [orgId], null);
  const canPayOnline = hasModule("online_payment") && paySettings?.enabled !== false;

  const unpaid = (invoices || []).filter((row) => row.status !== "paid");
  const paid = (invoices || []).filter((row) => row.status === "paid");

  const markPaid = async (qr) => {
    if (!payInvoice) return;
    await settleInvoicePayment({ orgId, invoiceId: payInvoice.id, transactionId: qr?.transactionId, amount: qr?.amount, refNo: qr?.refNo, organization });
    setPayInvoice(null);
    reload();
    toast(tx("ชำระเงินสำเร็จ", "Payment successful"), "success");
  };

  return (
    <>
      <Tabs
        tabs={[
          { key: "unpaid", label: tx("รอชำระ", "Unpaid"), count: unpaid.length },
          { key: "paid", label: tx("ชำระแล้ว", "Paid"), count: paid.length },
          { key: "receipts", label: tx("ใบเสร็จ", "Receipts"), count: receipts?.length },
          ...(taxDocs?.length ? [{ key: "tax", label: tx("ใบกำกับภาษี", "Tax invoices"), count: taxDocs.length }] : []),
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === "unpaid" &&
        (unpaid.length === 0 ? (
          <EmptyState icon={<Icon name="success" />} text={tx("ไม่มีบิลค้างชำระ", "No unpaid bills")} />
        ) : (
          unpaid.map((row) => (
            <Card key={row.id}>
              <div className="u-between">
                <div>
                  <div className="u-strong">{row.docNo}</div>
                  <div className="u-fs-13 u-muted">{tx("ครบกำหนด", "Due")} {formatDate(row.dueAt)}</div>
                </div>
                <StatusBadge status={row.status} />
              </div>
              <div className="u-between u-mt-8" style={{ fontSize: 18, fontWeight: 800 }}>
                <span>{baht(row.total)}</span>
                {canPayOnline ? <Button variant="primary" size="sm" onClick={() => setPayInvoice(row)}>{tx("ชำระเงิน", "Pay")}</Button> : <span className="u-fs-12 u-muted">{tx("ติดต่อร้านเพื่อชำระเงิน", "Contact the shop to pay")}</span>}
              </div>
            </Card>
          ))
        ))}

      {tab === "paid" &&
        (paid.length === 0 ? (
          <EmptyState text={tx("ยังไม่มีประวัติการชำระ", "No payment history")} />
        ) : (
          paid.map((row) => (
            <Card key={row.id}>
              <div className="u-between">
                <div>
                  <div className="u-strong">{row.docNo}</div>
                  <div className="u-fs-13 u-muted">{formatDate(row.paidAt)}</div>
                </div>
                <span className="u-strong">{baht(row.total)}</span>
              </div>
            </Card>
          ))
        ))}

      {tab === "receipts" &&
        (!receipts?.length ? (
          <EmptyState text={tx("ยังไม่มีใบเสร็จ", "No receipts")} />
        ) : (
          receipts.map((row) => (
            <Card key={row.id}>
              <div className="u-between">
                <div>
                  <div className="u-strong">{row.docNo}</div>
                  <div className="u-fs-13 u-muted">{tx("อ้างอิง", "Ref")} {row.invoiceNo}</div>
                </div>
                <span className="u-strong">{baht(row.total)}</span>
              </div>
            </Card>
          ))
        ))}

      {tab === "tax" ? (
        taxDocs.map((row) => (
          <Card key={row.id}>
            <div className="u-between">
              <div>
                <div className="u-strong">{row.docNo}</div>
                <div className="u-fs-13 u-muted">{TAX_DOC_TYPES[row.docType]?.label} · {formatDate(row.issuedAt)}</div>
              </div>
              <div className="u-flex u-gap-4" style={{ alignItems: "center" }}>
                <span className="u-strong">{baht(row.total)}</span>
                <Button size="sm" onClick={() => setTaxView(row)}>{tx("ดู/พิมพ์", "View / print")}</Button>
              </div>
            </div>
          </Card>
        ))
      ) : null}

      <Modal
        open={!!taxView}
        size="lg"
        title={taxView?.docNo}
        onClose={() => setTaxView(null)}
        footer={<Button variant="primary" onClick={() => setPrintToken(Date.now())}>{tx("พิมพ์", "Print")}</Button>}
      >
        <TaxDocumentPaper doc={taxView} brand={brand} copy="copy" />
      </Modal>
      <PrintArea token={printToken} onDone={() => setPrintToken(0)}>
        <TaxDocumentPaper doc={taxView} brand={brand} copy="copy" />
      </PrintArea>

      <PaymentQrModal
        open={!!payInvoice}
        title={tx(`ชำระ ${payInvoice?.docNo}`, `Pay ${payInvoice?.docNo}`)}
        amount={payInvoice?.total || 0}
        orderType={ORDER_TYPE.invoice}
        reference={payInvoice?.docNo}
        items={[{ productName: payInvoice?.docNo, quantity: 1, price: payInvoice?.total || 0 }]}
        orgId={orgId}
        onClose={() => setPayInvoice(null)}
        onPaid={markPaid}
      />
    </>
  );
}
